import { useRef, useState } from "react";

import { NumberInput } from "@impero-com/common/components/form/fields/numberInput";
import { SelectInput } from "@impero-com/common/components/form/fields/selectInput";
import { useTranslation } from "@impero-com/common/utils/i18n";

import {
    ControlNotificationUnit,
    MAX_NOTIFICATION_OFFSET_DAY_COUNT,
    SimpleAllocatedTime,
} from "../formData";

import { InputWithRightText } from "./InputWithRightText";
import * as theme from "./styles.css";

export interface AllocatedTimePickerReviewerProps {
    target: "firstReviewer" | "secondReviewer";
    value: SimpleAllocatedTime;
    onChange: (value: SimpleAllocatedTime) => void;
    calendarName?: string;
    disabled?: boolean;
}

export function AllocatedTimePickerReviewer({
    target,
    value: allocatedTime,
    onChange,
    calendarName,
    disabled,
}: AllocatedTimePickerReviewerProps) {
    const { t } = useTranslation();
    // Bias ref: when value is 0, remember the last selected unit so we can display "hours" correctly
    const biasRef = useRef<ControlNotificationUnit>(allocatedTime.unit);

    const displayUnit =
        // eslint-disable-next-line react-hooks/refs
        allocatedTime.value === 0 ? biasRef.current : allocatedTime.unit;
    const [currentUnit, setCurrentUnit] =
        useState<ControlNotificationUnit>(displayUnit);

    const max =
        currentUnit === "hours" ? 23 : MAX_NOTIFICATION_OFFSET_DAY_COUNT;

    const isSecondReviewer = target === "secondReviewer";
    const transTarget = isSecondReviewer
        ? t("CreateControl_Notification_SecondReviewerHas")
        : t("CreateControl_Notification_FirstReviewerHas");
    const taskLabel = isSecondReviewer
        ? t("CreateControl_Notification_Task_SecondReview")
        : t("CreateControl_Notification_Task_FirstReview");

    const toPerform = t("CreateControl_Notification_AssigneeToPerform", {
        taskLabel,
    });

    const options = (["days", "hours"] as ControlNotificationUnit[]).map(
        (daysOrHours) => {
            const optionIsDays = daysOrHours === "days";
            const label = optionIsDays
                ? (calendarName ??
                  t("Controls_Schedule_Day", { count: allocatedTime.value }))
                : t("Controls_Schedule_Hour", { count: allocatedTime.value });
            return {
                label,
                value: daysOrHours,
            };
        }
    );

    const handleValueChange = (newValue: number | null) => {
        onChange({ ...allocatedTime, unit: currentUnit, value: newValue ?? 0 });
    };

    const handleUnitChange = (newUnit: ControlNotificationUnit) => {
        biasRef.current = newUnit;
        setCurrentUnit(newUnit);
        onChange({ ...allocatedTime, unit: newUnit });
    };

    const selectAfter = (
        <SelectInput
            value={currentUnit}
            onChange={handleUnitChange}
            options={options}
            disabled={disabled}
            dropdownMatchSelectWidth
        />
    );

    return (
        <div className={theme.inputWithTextContainer}>
            <InputWithRightText label={transTarget} rightText={toPerform}>
                <NumberInput
                    className={theme.inputWithAddonAfter}
                    value={allocatedTime.value}
                    onChange={handleValueChange}
                    disabled={disabled}
                    precision={0}
                    addonAfter={selectAfter}
                    min={0}
                    max={max}
                />
            </InputWithRightText>
        </div>
    );
}
